import { type FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  type DashboardPendingInvite,
  acceptInviteAsUser,
  declineInviteAsUser,
  getInviteByTokenAsUser,
} from "../../dashboard-client";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { LoadingState } from "../common/LoadingState";

const InviteCardBody: FC<{
  invite: DashboardPendingInvite | null;
  error: string | null;
  isNotPending: boolean | null | undefined;
  isExpired: boolean | undefined;
  submitting: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onGoHome: () => void;
  onGoOverview: () => void;
}> = ({
  invite,
  error,
  isNotPending,
  isExpired,
  submitting,
  onAccept,
  onDecline,
  onGoHome,
  onGoOverview,
}) => {
  if (!invite || error) {
    return (
      <div className="space-y-4">
        <h2 className="font-mono text-sm uppercase tracking-[0.24em] text-slate-800 dark:text-slate-100">
          Invite not found
        </h2>
        <Alert variant="error">
          {error ?? "This invite link is invalid or has expired."}
        </Alert>
        <Button variant="secondary" fullWidth onClick={onGoHome}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  if (isNotPending) {
    return (
      <div className="space-y-4">
        <h2 className="font-mono text-sm uppercase tracking-[0.24em] text-slate-800 dark:text-slate-100">
          Invite already {invite.status}
        </h2>
        <p className="text-sm text-gray-500 dark:text-white/50">
          This invite to join{" "}
          <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
            {invite.organization_name}
          </span>{" "}
          has already been {invite.status}.
        </p>
        <Button fullWidth onClick={onGoOverview}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="space-y-4">
        <h2 className="font-mono text-sm uppercase tracking-[0.24em] text-slate-800 dark:text-slate-100">
          Invite expired
        </h2>
        <p className="text-sm text-gray-500 dark:text-white/50">
          This invite to join{" "}
          <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
            {invite.organization_name}
          </span>{" "}
          has expired. Please ask for a new invite.
        </p>
        <Button variant="secondary" fullWidth onClick={onGoHome}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-mono text-sm uppercase tracking-[0.24em] text-slate-800 dark:text-slate-100">
        You've been invited
      </h2>
      <p className="text-sm text-gray-500 dark:text-white/50">
        {invite.inviter_name ?? "A team member"} invited you to join{" "}
        <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {invite.organization_name}
        </span>{" "}
        as a {invite.role}.
      </p>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex gap-3">
        <Button
          fullWidth
          onClick={onAccept}
          loading={submitting}
          loadingText="Accepting..."
        >
          Accept Invite
        </Button>
        <Button
          variant="secondary"
          fullWidth
          onClick={onDecline}
          disabled={submitting}
        >
          Decline
        </Button>
      </div>
    </div>
  );
};

export const InvitePage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useDashboardAuth();
  const token = searchParams.get("token") ?? "";

  const [invite, setInvite] = useState<DashboardPendingInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("No invite token provided.");
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const inviteResponse = await getInviteByTokenAsUser({
          path: { token },
        });
        const details = inviteResponse.data;
        setInvite(details);
      } catch (error) {
        setLoadError(
          error instanceof Error ? error.message : "Failed to load invite.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = useCallback(() => {
    if (!token) {
      return;
    }
    setSubmitting(true);
    setLoadError(null);
    void acceptInviteAsUser({ path: { token } })
      .then(() => auth.refreshSession())
      .then(() => void navigate("/overview"))
      .catch((error: unknown) => {
        setLoadError(
          error instanceof Error ? error.message : "Failed to accept invite.",
        );
        setSubmitting(false);
      });
  }, [token, auth, navigate]);

  const handleDecline = useCallback(() => {
    if (!token) {
      return;
    }
    setSubmitting(true);
    setLoadError(null);
    void declineInviteAsUser({ path: { token } })
      .then(() => void navigate("/"))
      .catch((error: unknown) => {
        setLoadError(
          error instanceof Error ? error.message : "Failed to decline invite.",
        );
        setSubmitting(false);
      });
  }, [token, navigate]);

  const goHome = useCallback(() => {
    void navigate("/");
  }, [navigate]);

  const goOverview = useCallback(() => {
    void navigate("/overview");
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingState variant="skeleton" rows={3} />
      </div>
    );
  }

  const isExpired =
    invite?.status === "pending" &&
    Boolean(invite.expires_at) &&
    new Date(invite.expires_at) < new Date();
  const isNotPending = invite && invite.status !== "pending";

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-12">
      <img src="/arti.svg" alt="Arti" className="mb-6 h-10 w-10" />

      <div className="w-full rounded-none border border-slate-300 bg-white p-6 shadow-[6px_6px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/50 dark:shadow-[6px_6px_0_rgba(15,23,42,0.24)]">
        <InviteCardBody
          invite={invite}
          error={loadError}
          isNotPending={isNotPending}
          isExpired={isExpired}
          submitting={submitting}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onGoHome={goHome}
          onGoOverview={goOverview}
        />
      </div>
    </div>
  );
};
